import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, User, Calendar, Weight, Stethoscope, FileText, Activity, Eye, ClipboardList, CalendarClock, ChevronDown, ChevronUp, ExternalLink, Plus, Syringe, Pill, Package, QrCode, RefreshCw } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';

export const PetView = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from'); 
  const navigate = useNavigate();
  const [pet, setPet] = useState(null);
  const [owner, setOwner] = useState(null);
  const [petType, setPetType] = useState(null);
  const [diagnoses, setDiagnoses] = useState([]);
  const [visits, setVisits] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [visitTypes, setVisitTypes] = useState([]);
  const [services, setServices] = useState([]);
  const [allDiagnoses, setAllDiagnoses] = useState([]);
  const [allVaccinations, setAllVaccinations] = useState([]);
  const [allPrescriptions, setAllPrescriptions] = useState([]);
  const [allTreatments, setAllTreatments] = useState([]);
  const [allVisitServices, setAllVisitServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedVisitId, setExpandedVisitId] = useState(null);
  const [visitDetails, setVisitDetails] = useState({});

  // QR image upload
  const [uploadToken, setUploadToken] = useState(null);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [isCheckingQr, setIsCheckingQr] = useState(false);

  // Add Visit dialog
  const [visitDialogOpen, setVisitDialogOpen] = useState(false);
  const [visitForm, setVisitForm] = useState({
    visit_type_id: '',
    veterinarian_id: '',
    service_id: '',
    status: 'Scheduled',
    visit_date: '',
    notes: '',
  });

  // Add Schedule dialog
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    doctor_id: '',
    schedule_datetime: '',
    description: '',
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch the specific pet first to get owner_id and pet_type_id
        const petRes = await api.get(`/pets/${id}`);
        const foundPet = petRes.data;
        setPet(foundPet);

        // Fetch all data targeted to this pet in parallel
        const [ownerRes, petTypesRes, doctorsRes, visitTypesRes, servicesRes,
               legacyDiagnosesRes, visitsRes, schedulesRes,
               diagnosesRes, vaccinationsRes, prescriptionsRes, treatmentsRes, visitServicesRes] = await Promise.all([
          api.get(`/owners/${foundPet.owner_id}`),
          api.get('/pet-types'),
          api.get('/doctors'),
          api.get('/visit-types'),
          api.get('/services'),
          api.get(`/diagnoses?pet_id=${id}`),
          api.get(`/pets/${id}/visits`),
          api.get(`/pets/${id}/schedules`),
          api.get(`/pets/${id}/diagnoses`),
          api.get(`/pets/${id}/vaccinations`),
          api.get(`/pets/${id}/prescriptions`),
          api.get(`/pets/${id}/treatments`),
          api.get(`/pets/${id}/visit-services`),
        ]);

        setOwner(ownerRes.data);
        setPetType(petTypesRes.data.find((pt) => pt.id === foundPet.pet_type_id));
        setDoctors(doctorsRes.data);
        setVisitTypes(visitTypesRes.data);
        setServices(servicesRes.data);
        setDiagnoses(legacyDiagnosesRes.data);
        setVisits(visitsRes.data);
        setSchedules(schedulesRes.data);
        setAllDiagnoses(diagnosesRes.data);
        setAllVaccinations(vaccinationsRes.data);
        setAllPrescriptions(prescriptionsRes.data);
        setAllTreatments(treatmentsRes.data);
        setAllVisitServices(visitServicesRes.data);
      } catch (error) {
        toast.error('Failed to load pet details');
        navigate('/dashboard/pets');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, navigate]);

  const getDoctorName = (doctorId) => {
    return doctors.find((d) => d.id === doctorId)?.full_name || 'Unknown';
  };

  const getVisitTypeName = (visitTypeId) => {
    return visitTypes.find((vt) => vt.id === visitTypeId)?.name || 'Unknown';
  };

  const getServiceName = (serviceId) => {
    return services.find((s) => s.id === serviceId)?.name || 'Unknown';
  };

  const generateUploadQr = async () => {
    try {
      const response = await api.post('/upload-sessions');
      setUploadToken(response.data.token);
      setQrDialogOpen(true);
    } catch (error) {
      toast.error('Failed to generate QR code. Please try again.');
    }
  };

  const checkForImage = async () => {
    if (!uploadToken) return;
    setIsCheckingQr(true);
    try {
      const response = await api.get(`/upload-sessions/${uploadToken}`);
      if (response.data.image_url) {
        const imageUrl = response.data.image_url;
        // Persist to DB
        await api.put(`/pets/${id}`, {
          name: pet.name,
          date_of_birth: pet.date_of_birth,
          pet_type_id: pet.pet_type_id,
          owner_id: pet.owner_id,
          image_url: imageUrl,
          breed: pet.breed,
          gender: pet.gender,
          weight: pet.weight,
          color: pet.color,
        });
        setPet((prev) => ({ ...prev, image_url: imageUrl }));
        setQrDialogOpen(false);
        setUploadToken(null);
        toast.success('Photo updated successfully!');
      } else {
        toast.info('No photo yet — complete the upload on your phone first.');
      }
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (error.response?.status === 404 || detail.toLowerCase().includes('expired')) {
        toast.error('QR code expired. Please generate a new one.');
        setQrDialogOpen(false);
        setUploadToken(null);
      } else {
        toast.error('Failed to check for image. Please try again.');
      }
    } finally {
      setIsCheckingQr(false);
    }
  };

  const handleAddVisit = async (e) => {
    e.preventDefault();
    if (!visitForm.visit_type_id) {
      toast.error('Please select a visit type');
      return;
    }
    try {
      await api.post('/visits', {
        ...visitForm,
        pet_id: id,
        owner_id: owner?.id,
      });
      toast.success('Visit added successfully');
      setVisitDialogOpen(false);
      setVisitForm({ visit_type_id: '', veterinarian_id: '', service_id: '', status: 'Scheduled', visit_date: '', notes: '' });
      // Refresh visits
      const visitsRes = await api.get('/visits');
      setVisits(visitsRes.data.filter((v) => v.pet_id === id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add visit');
    }
  };

  const handleAddSchedule = async (e) => {
    e.preventDefault();
    if (!scheduleForm.schedule_datetime) {
      toast.error('Please select a date and time');
      return;
    }
    try {
      await api.post('/schedules', {
        ...scheduleForm,
        pet_id: id,
        owner_id: owner?.id,
      });
      toast.success('Schedule added successfully');
      setScheduleDialogOpen(false);
      setScheduleForm({ doctor_id: '', schedule_datetime: '', description: '' });
      // Refresh schedules
      const schedulesRes = await api.get('/schedules');
      setSchedules(schedulesRes.data.filter((s) => s.pet_id === id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add schedule');
    }
  };

  const toggleVisitDetails = async (visitId) => {
    if (expandedVisitId === visitId) {
      setExpandedVisitId(null);
      return;
    }
    setExpandedVisitId(visitId);
    if (visitDetails[visitId] && !visitDetails[visitId].loading) return;
    setVisitDetails((prev) => ({ ...prev, [visitId]: { loading: true } }));
    try {
      const [diagRes, treatRes, prescRes, vaccRes, svcRes] = await Promise.all([
        api.get(`/visits/${visitId}/diagnoses`),
        api.get(`/visits/${visitId}/treatments`),
        api.get(`/visits/${visitId}/prescriptions`),
        api.get(`/visits/${visitId}/vaccinations`),
        api.get(`/visits/${visitId}/services`),
      ]);
      setVisitDetails((prev) => ({
        ...prev,
        [visitId]: {
          loading: false,
          diagnoses: diagRes.data,
          treatments: treatRes.data,
          prescriptions: prescRes.data,
          vaccinations: vaccRes.data,
          services: svcRes.data,
        },
      }));
    } catch {
      setVisitDetails((prev) => ({ ...prev, [visitId]: { loading: false, error: true } }));
      toast.error('Failed to load visit details');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'Scheduled': 'bg-blue-100 text-blue-800',
      'In Progress': 'bg-yellow-100 text-yellow-800',
      'Completed': 'bg-green-100 text-green-800',
      'Cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const calcAge = (dob) => {
    if (!dob) return null;
    const diff = Date.now() - new Date(dob).getTime();
    const years = Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
    if (years === 0) {
      const months = Math.floor(diff / (1000 * 60 * 60 * 24 * 30.44));
      return `${months} month${months !== 1 ? 's' : ''}`;
    }
    return `${years} year${years !== 1 ? 's' : ''}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!pet) return null;

  return (
    <div className="p-6 md:p-12" data-testid="pet-view-page">
      {/* Back button */}
      <Button
        variant="ghost"
        onClick={() => navigate(from === 'owner' ? `/dashboard/owners/${pet.owner_id}` : '/dashboard/pets')}
        className="mb-6 text-sage-700 hover:text-sage-900 hover:bg-sage-50 -ml-2"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        {from === 'owner' ? 'Back to Owner' : 'Back to Pets'}
      </Button>

      {/* Pet Card */}
      <div className="bg-white rounded-3xl shadow-lg p-5 md:p-8 mb-8 border border-stone-100">
        <div className="flex flex-col sm:flex-row items-start gap-4 md:gap-6">
          {/* Avatar/Image */}
          <div className="flex flex-col items-center gap-2 flex-shrink-0">
            <div className="w-24 h-24 rounded-2xl bg-sage-100 flex items-center justify-center overflow-hidden">
              {pet.image_url ? (
                <img 
                  src={pet.image_url} 
                  alt={pet.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <span 
                className="text-4xl font-bold text-sage-600"
                style={{ display: pet.image_url ? 'none' : 'flex' }}
              >
                {pet.name?.charAt(0)?.toUpperCase()}
              </span>
            </div>
            <button
              type="button"
              onClick={generateUploadQr}
              className="flex items-center gap-1 text-xs text-sage-600 hover:text-sage-800 font-medium px-2 py-1 rounded-lg hover:bg-sage-50 transition-colors"
              title="Upload photo via QR code"
            >
              <QrCode className="w-3.5 h-3.5" />
              {pet.image_url ? 'Change photo' : 'Add photo'}
            </button>
          </div>

          {/* Info */}
          <div className="flex-1">
            <div className="flex flex-wrap items-start justify-between mb-1">
              <h1 className="text-3xl font-heading font-bold text-sage-800">
                {pet.name}
              </h1>
              <span className="bg-sage-100 text-sage-700 text-sm font-semibold px-3 py-1 rounded-full">
                {petType?.name || 'Unknown Type'}
              </span>
            </div>
            <p className="text-gray-500 mb-4">
              {calcAge(pet.date_of_birth) ? `${calcAge(pet.date_of_birth)} old` : 'Age unknown'}
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="flex items-center gap-2 text-gray-600">
                <User className="w-4 h-4 text-sage-500 flex-shrink-0" />
                <div>
                  <div className="text-xs text-gray-500">Owner</div>
                  <span className="text-sm font-medium">{owner?.full_name || '—'}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Calendar className="w-4 h-4 text-sage-500 flex-shrink-0" />
                <div>
                  <div className="text-xs text-gray-500">Date of Birth</div>
                  <span className="text-sm font-medium">{formatDate(pet.date_of_birth)}</span>
                </div>
              </div>
              {pet.breed && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Activity className="w-4 h-4 text-sage-500 flex-shrink-0" />
                  <div>
                    <div className="text-xs text-gray-500">Breed</div>
                    <span className="text-sm font-medium">{pet.breed}</span>
                  </div>
                </div>
              )}
              {pet.gender && (
                <div className="flex items-center gap-2 text-gray-600">
                  <User className="w-4 h-4 text-sage-500 flex-shrink-0" />
                  <div>
                    <div className="text-xs text-gray-500">Gender</div>
                    <span className="text-sm font-medium">{pet.gender}</span>
                  </div>
                </div>
              )}
              {pet.weight && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Weight className="w-4 h-4 text-sage-500 flex-shrink-0" />
                  <div>
                    <div className="text-xs text-gray-500">Weight</div>
                    <span className="text-sm font-medium">{pet.weight} kg</span>
                  </div>
                </div>
              )}
              {pet.color && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Eye className="w-4 h-4 text-sage-500 flex-shrink-0" />
                  <div>
                    <div className="text-xs text-gray-500">Color</div>
                    <span className="text-sm font-medium">{pet.color}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Section */}
      <Tabs defaultValue="service-records" className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="service-records" className="flex items-center gap-2">
            <Stethoscope className="w-4 h-4" />
            Service Records
          </TabsTrigger>
          <TabsTrigger value="visits" className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4" />
            Visits
          </TabsTrigger>
          <TabsTrigger value="schedules" className="flex items-center gap-2">
            <CalendarClock className="w-4 h-4" />
            Schedules
          </TabsTrigger>
        </TabsList>

        {/* Service Records Tab */}
        <TabsContent value="service-records">
          <div>
            <div className="flex items-center gap-2 mb-6">
              <Stethoscope className="w-5 h-5 text-sage-600" />
              <h2 className="text-2xl font-heading font-bold text-sage-800">Service History</h2>
              <span className="ml-2 bg-sage-100 text-sage-700 text-sm font-semibold px-3 py-0.5 rounded-full">
                {diagnoses.length + allDiagnoses.length + allVaccinations.length + allPrescriptions.length + allTreatments.length + allVisitServices.length}
              </span>
            </div>

            {diagnoses.length + allDiagnoses.length + allVaccinations.length + allPrescriptions.length + allTreatments.length + allVisitServices.length === 0 ? (
              <div className="bg-white rounded-3xl shadow-sm border border-stone-100 p-12 text-center">
                <Stethoscope className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No service records for this pet yet.</p>
              </div>
            ) : (
              <div className="space-y-8">

                {/* Legacy visit notes (from diagnoses endpoint) */}
                {diagnoses.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <FileText className="w-4 h-4 text-sage-600" />
                      <h3 className="text-sm font-semibold uppercase tracking-wide text-sage-600">Visit Notes</h3>
                      <span className="bg-sage-100 text-sage-700 text-xs font-semibold px-2 py-0.5 rounded-full">{diagnoses.length}</span>
                    </div>
                    <div className="space-y-3">
                      {diagnoses
                        .sort((a, b) => new Date(b.visit_date) - new Date(a.visit_date))
                        .map((d, idx) => (
                          <div key={d.id || idx} className="bg-white rounded-2xl border border-stone-100 p-5 shadow-sm">
                            <div className="flex items-start justify-between mb-1">
                              <span className="font-semibold text-sage-800">{formatDate(d.visit_date)}</span>
                              {d.pet_weight && (
                                <span className="flex items-center gap-1 text-xs text-gray-500">
                                  <Weight className="w-3 h-3" />{d.pet_weight} kg
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mt-1">{d.diagnosis_details}</p>
                            {d.doctor_id && (
                              <p className="text-xs text-gray-400 mt-1">Dr. {getDoctorName(d.doctor_id)}</p>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Diagnoses */}
                {allDiagnoses.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Activity className="w-4 h-4 text-blue-600" />
                      <h3 className="text-sm font-semibold uppercase tracking-wide text-blue-600">Diagnoses</h3>
                      <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full">{allDiagnoses.length}</span>
                    </div>
                    <div className="space-y-3">
                      {allDiagnoses
                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                        .map((d, idx) => (
                          <div key={d.id || idx} className="bg-white rounded-2xl border border-blue-100 p-5 shadow-sm">
                            <div className="flex items-start justify-between mb-1">
                              <span className="font-semibold text-blue-900">{d.diagnosis_name}</span>
                              <span className="text-xs text-gray-400">{formatDate(d.created_at)}</span>
                            </div>
                            {d.description && <p className="text-sm text-blue-800 mt-1">{d.description}</p>}
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Vaccinations */}
                {allVaccinations.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Syringe className="w-4 h-4 text-amber-600" />
                      <h3 className="text-sm font-semibold uppercase tracking-wide text-amber-600">Vaccinations</h3>
                      <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-0.5 rounded-full">{allVaccinations.length}</span>
                    </div>
                    <div className="space-y-3">
                      {allVaccinations
                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                        .map((v, idx) => (
                          <div key={v.id || idx} className="bg-white rounded-2xl border border-amber-100 p-5 shadow-sm">
                            <div className="flex items-start justify-between mb-1">
                              <span className="font-semibold text-amber-900">{v.vaccine_name}</span>
                              <span className="text-xs text-gray-400">{formatDate(v.created_at)}</span>
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-sm text-amber-800">
                              {v.manufacturer && <span>Manufacturer: {v.manufacturer}</span>}
                              {v.batch_number && <span>Batch: {v.batch_number}</span>}
                              {v.next_due_date && <span>Next due: {formatDate(v.next_due_date)}</span>}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Prescriptions */}
                {allPrescriptions.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Pill className="w-4 h-4 text-purple-600" />
                      <h3 className="text-sm font-semibold uppercase tracking-wide text-purple-600">Prescriptions</h3>
                      <span className="bg-purple-100 text-purple-700 text-xs font-semibold px-2 py-0.5 rounded-full">{allPrescriptions.length}</span>
                    </div>
                    <div className="space-y-3">
                      {allPrescriptions
                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                        .map((p, idx) => (
                          <div key={p.id || idx} className="bg-white rounded-2xl border border-purple-100 p-5 shadow-sm">
                            <div className="flex items-start justify-between mb-1">
                              <span className="font-semibold text-purple-900">{p.medication_name}</span>
                              <span className="text-xs text-gray-400">{formatDate(p.created_at)}</span>
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1 text-sm text-purple-800">
                              {p.dosage && <span>Dosage: {p.dosage}</span>}
                              {p.duration && <span>Duration: {p.duration}</span>}
                            </div>
                            {p.instructions && <p className="text-sm text-purple-700 mt-1">{p.instructions}</p>}
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Treatments */}
                {allTreatments.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Stethoscope className="w-4 h-4 text-green-600" />
                      <h3 className="text-sm font-semibold uppercase tracking-wide text-green-600">Treatments</h3>
                      <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-full">{allTreatments.length}</span>
                    </div>
                    <div className="space-y-3">
                      {allTreatments
                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                        .map((t, idx) => (
                          <div key={t.id || idx} className="bg-white rounded-2xl border border-green-100 p-5 shadow-sm">
                            <div className="flex items-start justify-between mb-1">
                              <span className="font-semibold text-green-900">{t.treatment_name}</span>
                              <span className="text-xs text-gray-400">{formatDate(t.created_at)}</span>
                            </div>
                            {t.performed_by && <p className="text-sm text-green-700 mt-0.5">By: {t.performed_by}</p>}
                            {t.description && <p className="text-sm text-green-800 mt-1">{t.description}</p>}
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Services */}
                {allVisitServices.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Package className="w-4 h-4 text-sage-600" />
                      <h3 className="text-sm font-semibold uppercase tracking-wide text-sage-600">Services</h3>
                      <span className="bg-sage-100 text-sage-700 text-xs font-semibold px-2 py-0.5 rounded-full">{allVisitServices.length}</span>
                    </div>
                    <div className="space-y-3">
                      {allVisitServices
                        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                        .map((s, idx) => (
                          <div key={s.id || idx} className="bg-white rounded-2xl border border-stone-100 p-5 shadow-sm flex items-center justify-between">
                            <span className="font-semibold text-sage-900">{getServiceName(s.service_id)}</span>
                            <div className="flex items-center gap-3 text-sm text-sage-700">
                              {s.quantity && <span>×{s.quantity}</span>}
                              {s.price != null && <span>${s.price}</span>}
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

              </div>
            )}
          </div>
        </TabsContent>

        {/* Visits Tab */}
        <TabsContent value="visits">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-sage-600" />
                <h2 className="text-2xl font-heading font-bold text-sage-800">
                  Visits
                </h2>
                <span className="ml-2 bg-sage-100 text-sage-700 text-sm font-semibold px-3 py-0.5 rounded-full">
                  {visits.length}
                </span>
              </div>
              <Button
                className="bg-sage-500 hover:bg-sage-600 text-white rounded-full shadow"
                onClick={() => setVisitDialogOpen(true)}
                size="sm"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Visit
              </Button>
            </div>

            {visits.length === 0 ? (
              <div className="bg-white rounded-3xl shadow-sm border border-stone-100 p-12 text-center">
                <ClipboardList className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No visits recorded for this pet yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {visits
                  .sort((a, b) => new Date(b.visit_date) - new Date(a.visit_date))
                  .map((visit, idx) => (
                    <div
                      key={visit.id || idx}
                      className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6 hover:shadow-md transition-shadow mb-2"
                    >
                      {/* Card header row */}
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-5 h-5 text-sage-600" />
                          <h3 className="font-semibold text-lg text-sage-800">
                            {formatDate(visit.visit_date)}
                          </h3>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(visit.status)}`}>
                            {visit.status}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-sage-600 hover:text-sage-800 hover:bg-sage-50 p-1 h-auto"
                            onClick={() => navigate(`/dashboard/visits/${visit.id}`)}
                            title="View full record"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>

                      <div className="grid gap-2 mb-3">
                        {visit.visit_type_id && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <FileText className="w-4 h-4" />
                            <span>Type: {getVisitTypeName(visit.visit_type_id)}</span>
                          </div>
                        )}
                        {visit.veterinarian_id && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <User className="w-4 h-4" />
                            <span>Dr. {getDoctorName(visit.veterinarian_id)}</span>
                          </div>
                        )}
                        {visit.service_id && (
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Stethoscope className="w-4 h-4" />
                            <span>Service: {getServiceName(visit.service_id)}</span>
                          </div>
                        )}
                      </div>

                      {visit.notes && (
                        <div className="bg-sage-50 rounded-xl p-4 mb-3">
                          <div className="flex items-start gap-2 mb-2">
                            <FileText className="w-4 h-4 text-sage-600 mt-0.5 flex-shrink-0" />
                            <h4 className="font-medium text-sage-800">Notes</h4>
                          </div>
                          <p className="text-gray-700 text-sm leading-relaxed pl-6">
                            {visit.notes}
                          </p>
                        </div>
                      )}

                      {/* Expand / collapse toggle */}
                      <button
                        onClick={() => toggleVisitDetails(visit.id)}
                        className="flex items-center gap-1.5 text-sm text-sage-600 hover:text-sage-800 font-medium mt-1"
                      >
                        {expandedVisitId === visit.id
                          ? <><ChevronUp className="w-4 h-4" /> Hide details</>
                          : <><ChevronDown className="w-4 h-4" /> Show details</>}
                      </button>

                      {/* Expanded detail panel */}
                      {expandedVisitId === visit.id && (
                        <div className="mt-4 border-t border-stone-100 pt-4 space-y-4">
                          {visitDetails[visit.id]?.loading && (
                            <div className="text-center text-gray-400 py-4 text-sm">Loading…</div>
                          )}
                          {visitDetails[visit.id]?.error && (
                            <div className="text-center text-red-400 py-4 text-sm">Failed to load details.</div>
                          )}
                          {visitDetails[visit.id] && !visitDetails[visit.id].loading && !visitDetails[visit.id].error && (
                            <>
                              {/* Diagnoses */}
                              {visitDetails[visit.id].diagnoses?.length > 0 && (
                                <div>
                                  <h4 className="text-xs font-semibold uppercase tracking-wide text-sage-600 mb-2 flex items-center gap-1.5">
                                    <Activity className="w-3.5 h-3.5" /> Diagnoses
                                  </h4>
                                  <div className="space-y-2">
                                    {visitDetails[visit.id].diagnoses.map((d, i) => (
                                      <div key={i} className="bg-blue-50 rounded-xl px-4 py-3">
                                        <div className="font-medium text-sm text-blue-900">{d.diagnosis_name}</div>
                                        {d.description && <div className="text-xs text-blue-700 mt-0.5">{d.description}</div>}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Treatments */}
                              {visitDetails[visit.id].treatments?.length > 0 && (
                                <div>
                                  <h4 className="text-xs font-semibold uppercase tracking-wide text-sage-600 mb-2 flex items-center gap-1.5">
                                    <Stethoscope className="w-3.5 h-3.5" /> Treatments
                                  </h4>
                                  <div className="space-y-2">
                                    {visitDetails[visit.id].treatments.map((t, i) => (
                                      <div key={i} className="bg-green-50 rounded-xl px-4 py-3">
                                        <div className="flex items-center justify-between">
                                          <div className="font-medium text-sm text-green-900">{t.treatment_name}</div>
                                          {t.performed_by && <div className="text-xs text-green-700">By: {t.performed_by}</div>}
                                        </div>
                                        {t.description && <div className="text-xs text-green-700 mt-0.5">{t.description}</div>}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Prescriptions */}
                              {visitDetails[visit.id].prescriptions?.length > 0 && (
                                <div>
                                  <h4 className="text-xs font-semibold uppercase tracking-wide text-sage-600 mb-2 flex items-center gap-1.5">
                                    <FileText className="w-3.5 h-3.5" /> Prescriptions
                                  </h4>
                                  <div className="space-y-2">
                                    {visitDetails[visit.id].prescriptions.map((p, i) => (
                                      <div key={i} className="bg-purple-50 rounded-xl px-4 py-3">
                                        <div className="font-medium text-sm text-purple-900">{p.medication_name}</div>
                                        <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5">
                                          {p.dosage && <span className="text-xs text-purple-700">Dosage: {p.dosage}</span>}
                                          {p.duration && <span className="text-xs text-purple-700">Duration: {p.duration}</span>}
                                        </div>
                                        {p.instructions && <div className="text-xs text-purple-700 mt-0.5">{p.instructions}</div>}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Vaccinations */}
                              {visitDetails[visit.id].vaccinations?.length > 0 && (
                                <div>
                                  <h4 className="text-xs font-semibold uppercase tracking-wide text-sage-600 mb-2 flex items-center gap-1.5">
                                    <Eye className="w-3.5 h-3.5" /> Vaccinations
                                  </h4>
                                  <div className="space-y-2">
                                    {visitDetails[visit.id].vaccinations.map((v, i) => (
                                      <div key={i} className="bg-amber-50 rounded-xl px-4 py-3">
                                        <div className="font-medium text-sm text-amber-900">{v.vaccine_name}</div>
                                        <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-0.5">
                                          {v.manufacturer && <span className="text-xs text-amber-700">Manufacturer: {v.manufacturer}</span>}
                                          {v.batch_number && <span className="text-xs text-amber-700">Batch: {v.batch_number}</span>}
                                          {v.next_due_date && <span className="text-xs text-amber-700">Next due: {formatDate(v.next_due_date)}</span>}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Services */}
                              {visitDetails[visit.id].services?.length > 0 && (
                                <div>
                                  <h4 className="text-xs font-semibold uppercase tracking-wide text-sage-600 mb-2 flex items-center gap-1.5">
                                    <ClipboardList className="w-3.5 h-3.5" /> Services
                                  </h4>
                                  <div className="space-y-2">
                                    {visitDetails[visit.id].services.map((s, i) => (
                                      <div key={i} className="bg-sage-50 rounded-xl px-4 py-3 flex items-center justify-between">
                                        <div className="font-medium text-sm text-sage-900">{getServiceName(s.service_id)}</div>
                                        <div className="flex items-center gap-3 text-xs text-sage-700">
                                          {s.quantity && <span>×{s.quantity}</span>}
                                          {s.price != null && <span>${s.price}</span>}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Empty state */}
                              {!visitDetails[visit.id].diagnoses?.length &&
                                !visitDetails[visit.id].treatments?.length &&
                                !visitDetails[visit.id].prescriptions?.length &&
                                !visitDetails[visit.id].vaccinations?.length &&
                                !visitDetails[visit.id].services?.length && (
                                <div className="text-center text-gray-400 py-4 text-sm">No detailed records for this visit yet.</div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Schedules Tab */}
        <TabsContent value="schedules">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CalendarClock className="w-5 h-5 text-sage-600" />
                <h2 className="text-2xl font-heading font-bold text-sage-800">
                  Schedules
                </h2>
                <span className="ml-2 bg-sage-100 text-sage-700 text-sm font-semibold px-3 py-0.5 rounded-full">
                  {schedules.length}
                </span>
              </div>
              <Button
                className="bg-sage-500 hover:bg-sage-600 text-white rounded-full shadow"
                onClick={() => setScheduleDialogOpen(true)}
                size="sm"
              >
                <Plus className="w-4 h-4 mr-1" /> Add Schedule
              </Button>
            </div>

            {schedules.length === 0 ? (
              <div className="bg-white rounded-3xl shadow-sm border border-stone-100 p-12 text-center">
                <CalendarClock className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No schedules for this pet yet.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {schedules
                  .sort((a, b) => new Date(b.schedule_datetime) - new Date(a.schedule_datetime))
                  .map((schedule, idx) => (
                    <div
                      key={schedule.id || idx}
                      className="bg-white rounded-2xl shadow-sm border border-stone-100 p-6 hover:shadow-md transition-shadow mb-2"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-5 h-5 text-sage-600" />
                          <h3 className="font-semibold text-lg text-sage-800">
                            {formatDateTime(schedule.schedule_datetime)}
                          </h3>
                        </div>
                      </div>

                      <div className="mb-3">
                        {schedule.doctor_id && (
                          <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                            <User className="w-4 h-4" />
                            <span>Dr. {getDoctorName(schedule.doctor_id)}</span>
                          </div>
                        )}
                      </div>

                      {schedule.description && (
                        <div className="bg-sage-50 rounded-xl p-4">
                          <div className="flex items-start gap-2 mb-2">
                            <FileText className="w-4 h-4 text-sage-600 mt-0.5 flex-shrink-0" />
                            <h4 className="font-medium text-sage-800">Description</h4>
                          </div>
                          <p className="text-gray-700 text-sm leading-relaxed pl-6">
                            {schedule.description}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* QR Image Upload Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={(open) => { setQrDialogOpen(open); if (!open) setUploadToken(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Upload Photo via Phone</DialogTitle>
          </DialogHeader>
          <div className="text-center space-y-4 py-2">
            <p className="text-sm text-gray-500">Scan this QR code with your phone to take or choose a photo.</p>
            {uploadToken && (
              <div className="flex justify-center">
                <QRCodeSVG
                  value={`${window.location.origin}/pet-upload/${uploadToken}`}
                  size={200}
                  bgColor="#f7faf7"
                  fgColor="#2d5a2d"
                />
              </div>
            )}
            {uploadToken && (
              <p className="text-xs text-gray-400 break-all">
                {`${window.location.origin}/pet-upload/${uploadToken}`}
              </p>
            )}
            <p className="text-xs text-gray-400">Link expires in 15 minutes</p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1 rounded-full"
                onClick={() => { setQrDialogOpen(false); setUploadToken(null); }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="flex-1 rounded-full bg-sage-500 hover:bg-sage-600 text-white"
                onClick={checkForImage}
                disabled={isCheckingQr}
              >
                <RefreshCw className={`w-4 h-4 mr-1.5 ${isCheckingQr ? 'animate-spin' : ''}`} />
                {isCheckingQr ? 'Checking...' : 'Check for Image'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Visit Dialog */}
      <Dialog open={visitDialogOpen} onOpenChange={setVisitDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Visit for {pet?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddVisit} className="space-y-4 mt-2">
            <div>
              <Label>Visit Type *</Label>
              <Select
                value={visitForm.visit_type_id}
                onValueChange={(v) => setVisitForm((prev) => ({ ...prev, visit_type_id: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select visit type" />
                </SelectTrigger>
                <SelectContent>
                  {visitTypes.map((vt) => (
                    <SelectItem key={vt.id} value={vt.id}>{vt.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Veterinarian</Label>
              <Select
                value={visitForm.veterinarian_id}
                onValueChange={(v) => setVisitForm((prev) => ({ ...prev, veterinarian_id: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select veterinarian" />
                </SelectTrigger>
                <SelectContent>
                  {doctors.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Service</Label>
              <Select
                value={visitForm.service_id}
                onValueChange={(v) => setVisitForm((prev) => ({ ...prev, service_id: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select service (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Status</Label>
              <Select
                value={visitForm.status}
                onValueChange={(v) => setVisitForm((prev) => ({ ...prev, status: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {['Scheduled', 'In Progress', 'Completed', 'Cancelled'].map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Visit Date</Label>
              <Input
                type="date"
                className="mt-1"
                value={visitForm.visit_date}
                onChange={(e) => setVisitForm((prev) => ({ ...prev, visit_date: e.target.value }))}
              />
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea
                className="mt-1"
                placeholder="Enter any notes…"
                value={visitForm.notes}
                onChange={(e) => setVisitForm((prev) => ({ ...prev, notes: e.target.value }))}
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setVisitDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-sage-500 hover:bg-sage-600 text-white">Add Visit</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Schedule Dialog */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Schedule for {pet?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddSchedule} className="space-y-4 mt-2">
            <div>
              <Label>Doctor</Label>
              <Select
                value={scheduleForm.doctor_id}
                onValueChange={(v) => setScheduleForm((prev) => ({ ...prev, doctor_id: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select doctor (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {doctors.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Date & Time *</Label>
              <Input
                type="datetime-local"
                className="mt-1"
                value={scheduleForm.schedule_datetime}
                onChange={(e) => setScheduleForm((prev) => ({ ...prev, schedule_datetime: e.target.value }))}
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                className="mt-1"
                placeholder="Enter description…"
                value={scheduleForm.description}
                onChange={(e) => setScheduleForm((prev) => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-sage-500 hover:bg-sage-600 text-white">Add Schedule</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
